import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { LoaderCircle, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { CREATE_PHOTO_TAG, DELETE_PHOTO_TAGS, MERGE_PHOTO_TAGS, PHOTO_TAGS, UPDATE_PHOTO_TAG } from '../api'
import { describePhotosError, photosErrorKey } from '../lib/errors'
import { sortTagsForPicker, TAG_COLORS, tagColorClass, tagNameTaken } from '../lib/tags'
import type { PhotoTag } from '../types'

// Literal keys (the i18n test scans for them).
const COLOR_LABEL: Record<string, string> = {
  gray: 'Photos_tag_color_gray',
  red: 'Photos_tag_color_red',
  orange: 'Photos_tag_color_orange',
  yellow: 'Photos_tag_color_yellow',
  green: 'Photos_tag_color_green',
  teal: 'Photos_tag_color_teal',
  blue: 'Photos_tag_color_blue',
  purple: 'Photos_tag_color_purple',
}

interface Props {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

const RenameInput: React.FC<{ tag: PhotoTag; disabled: boolean; onSave: (name: string) => Promise<boolean> }> = ({ tag, disabled, onSave }) => {
  const [draft, setDraft] = useState(tag.name)
  const cancelled = useRef(false)
  useEffect(() => setDraft(tag.name), [tag.name])

  const commit = () => {
    const name = draft.trim()
    if (cancelled.current || !name || name === tag.name) setDraft(tag.name)
    else onSave(name).then((ok) => !ok && setDraft(tag.name))
    cancelled.current = false
  }

  return (
    <Input
      value={draft}
      disabled={disabled}
      maxLength={40}
      data-photo-tag-rename=""
      className="h-8 text-sm"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur()
        if (e.key === 'Escape') {
          cancelled.current = true
          e.currentTarget.blur()
        }
      }}
    />
  )
}

const ColorSelect: React.FC<{ value: string; disabled: boolean; onChange: (color: string) => void }> = ({ value, disabled, onChange }) => {
  const { t } = useTranslation()
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-8 text-sm">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {TAG_COLORS.map((c) => (
          <SelectItem key={c} value={c}>
            <span className="flex items-center gap-2">
              <span className={cn('h-2.5 w-2.5 rounded-full', tagColorClass(c))} />
              {t(COLOR_LABEL[c])}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

const TagManagerDialog: React.FC<Props> = ({ projectId, open, onOpenChange }) => {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<string[]>([])
  const [target, setTarget] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState<string>('gray')

  const { data } = useQuery<{ photoTags: PhotoTag[] }>(PHOTO_TAGS, { variables: { projectId }, skip: !projectId || !open })
  const tags = sortTagsForPicker(data?.photoTags ?? [])
  const picked = tags.filter((x) => selected.indexOf(x.id) >= 0)
  const targets = tags.filter((x) => selected.indexOf(x.id) < 0)

  const opts = { context: { handlesErrors: true } }
  const [createTag, { loading: creating }] = useMutation(CREATE_PHOTO_TAG, { ...opts, refetchQueries: ['photoTags'] })
  const [updateTag] = useMutation(UPDATE_PHOTO_TAG, { ...opts, refetchQueries: ['photoTags'] })
  const [mergeTags, { loading: merging }] = useMutation(MERGE_PHOTO_TAGS, { ...opts, refetchQueries: ['photoTags', 'photos'] })
  const [deleteTags, { loading: deleting }] = useMutation(DELETE_PHOTO_TAGS, { ...opts, refetchQueries: ['photoTags', 'photos'] })

  useEffect(() => {
    if (!open) {
      setSelected([])
      setTarget('')
      setConfirmDelete(false)
      setNewName('')
      setNewColor('gray')
    }
  }, [open])

  const fail = (e: any) => toastr.error(t('Common_error'), describePhotosError(photosErrorKey(e), t))

  const update = async (id: string, patch: { name?: string; color?: string }) => {
    setBusyId(id)
    try {
      await updateTag({ variables: { id, ...patch } })
      return true
    } catch (e: any) {
      fail(e)
      return false
    } finally {
      setBusyId(null)
    }
  }

  const nameTaken = tagNameTaken(newName, tags)
  const create = async () => {
    const name = newName.trim()
    if (!name || nameTaken) return
    try {
      await createTag({ variables: { projectId, name, color: newColor } })
      setNewName('')
      setNewColor('gray')
    } catch (e: any) {
      fail(e)
    }
  }

  const toggle = (id: string) => {
    setSelected((cur) => (cur.indexOf(id) >= 0 ? cur.filter((x) => x !== id) : cur.concat(id)))
    setTarget('')
  }

  const merge = async () => {
    try {
      await mergeTags({ variables: { sourceIds: selected, targetId: target } })
      setSelected([])
      setTarget('')
    } catch (e: any) {
      fail(e)
    }
  }

  const remove = async () => {
    try {
      await deleteTags({ variables: { ids: selected } })
      setSelected([])
      setConfirmDelete(false)
    } catch (e: any) {
      fail(e)
    }
  }

  const working = merging || deleting

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !working && onOpenChange(o)}>
        <DialogContent
          className="sm:max-w-2xl"
          onEscapeKeyDown={(e) => {
            if ((e.target as HTMLElement)?.closest?.('[data-photo-tag-rename]')) e.preventDefault()
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('Photos_tags_manage')}</DialogTitle>
            <DialogDescription>{t('Photos_tags_manage_hint')}</DialogDescription>
          </DialogHeader>

          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              create()
            }}
          >
            <Input
              value={newName}
              maxLength={40}
              disabled={creating}
              placeholder={t('Photos_tags_new_placeholder')}
              aria-label={t('Photos_tags_new_placeholder')}
              className="h-8 flex-1 text-sm"
              onChange={(e) => setNewName(e.target.value)}
            />
            <div className="w-40 shrink-0">
              <ColorSelect value={newColor} disabled={creating} onChange={setNewColor} />
            </div>
            <Button type="submit" size="sm" className="h-8 shrink-0" disabled={creating || !newName.trim() || nameTaken} title={nameTaken ? t('Photos_tags_name_exists') : undefined}>
              {creating ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {t('Photos_tags_add')}
            </Button>
          </form>

          <div className="max-h-[50vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>{t('Photos_tags_name')}</TableHead>
                  <TableHead className="w-40">{t('Photos_tags_color')}</TableHead>
                  <TableHead className="w-20 text-right">{t('Photos_tags_usage')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tags.map((tag) => (
                  <TableRow key={tag.id}>
                    <TableCell>
                      <Checkbox checked={selected.indexOf(tag.id) >= 0} onCheckedChange={() => toggle(tag.id)} aria-label={tag.name} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <RenameInput tag={tag} disabled={busyId === tag.id} onSave={(name) => update(tag.id, { name })} />
                        {busyId === tag.id && <LoaderCircle className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />}
                      </div>
                    </TableCell>
                    <TableCell>
                      <ColorSelect value={tag.color} disabled={busyId === tag.id} onChange={(color) => update(tag.id, { color })} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{tag.usageCount}</TableCell>
                  </TableRow>
                ))}
                {!tags.length && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">{t('Photos_tags_none')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {picked.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t pt-4">
              <span className="text-sm text-muted-foreground">{t('Photos_selected', { count: picked.length })}</span>
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <Select value={target} onValueChange={setTarget} disabled={merging || !targets.length}>
                  <SelectTrigger className="h-8 w-[180px] text-sm">
                    <SelectValue placeholder={t('Photos_tags_merge_into')} />
                  </SelectTrigger>
                  <SelectContent>
                    {targets.map((x) => (
                      <SelectItem key={x.id} value={x.id}>
                        <span className="flex items-center gap-2">
                          <span className={cn('h-2.5 w-2.5 rounded-full', tagColorClass(x.color))} />
                          {x.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" className="h-8" disabled={!target || working} onClick={merge}>
                  {merging && <LoaderCircle className="h-4 w-4 animate-spin" />}
                  {t('Photos_tags_merge')}
                </Button>
                <Button variant="destructive" size="sm" className="h-8" disabled={working} onClick={() => setConfirmDelete(true)}>
                  {t('Common_delete')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDelete} onOpenChange={(o) => !deleting && setConfirmDelete(o)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('Photos_tags_delete_title', { count: picked.length })}</DialogTitle>
            <DialogDescription>{t('Photos_tags_delete_confirm')}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => setConfirmDelete(false)} disabled={deleting}>{t('Common_cancel')}</Button>
            <Button variant="destructive" onClick={remove} disabled={deleting}>
              {deleting && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {t('Common_delete')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default TagManagerDialog
